import { useNavigate } from "react-router-dom";
import { useLayoutEffect, useRef } from "react";
import gsap from "gsap";
import "./Hero.css";

export default function Hero() {
  const navigate = useNavigate();
  const rootRef = useRef(null);

  useLayoutEffect(() => {
    const ctx = gsap.context(() => {
      const tl = gsap.timeline({ defaults: { ease: "power4.out" } });

      tl.from(".hero-eyebrow", { y: 20, opacity: 0, duration: 0.7 }, 0.15)
        .from(".hero-title .line", { yPercent: 110, duration: 1, stagger: 0.12, ease: "expo.out" }, 0.3)
        .from(".hero-sub", { y: 20, opacity: 0, duration: 0.7 }, 0.85)
        .from(".hero-desc", { y: 16, opacity: 0, duration: 0.6 }, 0.95)
        .from(".hero-actions .btn", { y: 20, opacity: 0, duration: 0.6, stagger: 0.08 }, 1.05)
        .from(".hero-social a", { y: 14, opacity: 0, stagger: 0.06, duration: 0.5 }, 1.15)
        .from(".hero-visual", { scale: 0.96, opacity: 0, duration: 1, ease: "power3.out" }, 0.45)
        .from(".hero-portrait", { scale: 1.08, duration: 1.4, ease: "power3.out" }, 0.5)
        .from(".hero-float-card", { y: 30, opacity: 0, duration: 0.7, stagger: 0.12 }, 1.2)
        .from(".hero-scroll", { opacity: 0, duration: 0.6 }, 1.5);

      // subtle floating
      gsap.to(".hero-orb--1", { y: -14, duration: 3.5, repeat: -1, yoyo: true, ease: "sine.inOut" });
      gsap.to(".hero-orb--2", { y: 12, x: -8, duration: 4.2, repeat: -1, yoyo: true, ease: "sine.inOut", delay: 0.6 });
      gsap.to(".hero-float-card--a", { y: -6, duration: 2.8, repeat: -1, yoyo: true, ease: "sine.inOut" });
      gsap.to(".hero-float-card--b", { y: 8, duration: 3.2, repeat: -1, yoyo: true, ease: "sine.inOut", delay: 0.4 });

      // parallax on mouse
      const visual = document.querySelector(".hero-visual-inner");
      const onMove = (e) => {
        const { innerWidth: w, innerHeight: h } = window;
        const x = (e.clientX - w / 2) / w;
        const y = (e.clientY - h / 2) / h;
        gsap.to(visual, { x: x * 14, y: y * 10, duration: 0.8, ease: "power2.out", overwrite: "auto" });
        gsap.to(".hero-orb--1", { x: x * 22, y: y * 14, duration: 0.9, ease: "power2.out", overwrite: "auto" });
      };
      window.addEventListener("mousemove", onMove);
      return () => window.removeEventListener("mousemove", onMove);
    }, rootRef);

    return () => ctx.revert();
  }, []);

  return (
    <section ref={rootRef} className="hero">
      <div className="hero-bg">
        <div className="hero-grid" />
        <div className="hero-orb hero-orb--1" />
        <div className="hero-orb hero-orb--2" />
        <div className="hero-noise" />
      </div>

      <div className="hero-inner">
        <div className="hero-copy">
          <div className="hero-eyebrow eyebrow">Available for new opportunities · 2026</div>

          <h1 className="hero-title">
            <span className="line-wrap">
              <span className="line">Hey, I'm</span>
            </span>
            <span className="line-wrap">
              <span className="line grad-text">Yishak Hailu</span>
            </span>
          </h1>

          <p className="hero-sub">
            Full Stack Developer <span className="hero-sub-dot">•</span> React · Node · Mobile
          </p>

          <p className="hero-desc">
            I design and ship fast, accessible products — from polished frontends to resilient backends. Currently crafting web & mobile experiences in Addis Ababa.
          </p>

          <div className="hero-actions">
            <button className="btn btn-primary" onClick={() => navigate("/contact")}>
              <span>Hire me</span>
              <span className="btn-arrow">→</span>
            </button>
            <button className="btn btn-ghost" onClick={() => navigate("/projects")}>
              View projects
            </button>
          </div>

          <div className="hero-social">
            <a href="https://www.linkedin.com/in/yishak-hailu" target="_blank" rel="noreferrer" aria-label="LinkedIn">
              <img src="./assets/Linked-in-icon.png" alt="" />
              <span>LinkedIn</span>
            </a>
            <a href="https://www.instagram.com/_y.i.s.h.a.k_" target="_blank" rel="noreferrer" aria-label="Instagram">
              <img src="./assets/Instagram-icon.png" alt="" />
              <span>Instagram</span>
            </a>
            <a href="https://t.me/iamyishakg" target="_blank" rel="noreferrer" aria-label="Telegram">
              <img src="./assets/Telegram-icon.png" alt="" />
              <span>Telegram</span>
            </a>
          </div>
        </div>

        <div className="hero-visual">
          <div className="hero-visual-inner">
            <div className="hero-portrait-wrap">
              <img className="hero-portrait" src="https://i.postimg.cc/dVWjnj5R/20240420-163742.jpg" alt="Yishak Hailu" />
              <div className="hero-portrait-glow" />
            </div>

            <div className="hero-float-card hero-float-card--a">
              <span className="float-k">7+</span>
              <span className="float-v">
                <strong>Projects shipped</strong>
                <em>Production ready</em>
              </span>
            </div>
            <div className="hero-float-card hero-float-card--b">
              <span className="float-dot" />
              <span className="float-v">
                <strong>Open to work</strong>
                <em>Remote & onsite</em>
              </span>
            </div>
          </div>
        </div>
      </div>

      <div className="hero-scroll" aria-hidden>
        <span>Scroll</span>
        <i />
      </div>
    </section>
  );
}
